import { chunk } from 'lodash';

import { ProductType } from '../../../common';
import { Price, SFCCPricingSearchResult, SFCCProductPrice } from '../../models';
import { getPricingApiConfig } from '../pricing-api';
import { getPricesByIds } from './get-prices-by-ids';
import { getPricesBySkus } from './get-prices-by-skus';

/**
 * Fetches product prices for given product IDs, supporting chunking for large inputs.
 * @param productIds - An array of product IDs (numbers for non-SFCC pricing or strings for SFCC pricing).
 * @returns A Promise resolving to an array of Price or SFCCProductPrice, or null if no prices are found.
 */
export const getProductPrices = async (
  productIds: number[] | string[]
): Promise<Price[] | SFCCProductPrice[] | null> => {
  const { isSFCCPricingOn } = getPricingApiConfig();

  try {
    if (isSFCCPricingOn) {
      return await fetchSFCCPrices(productIds as string[]);
    } else {
      return await fetchStandardPrices(productIds as number[]);
    }
  } catch (error) {
    console.error(`Error fetching product prices for IDs ${productIds}:`, error);
    return null;
  }
};

/**
 * Fetches prices from SFCC API, with chunking for large SKU arrays.
 * @param skus - An array of SKUs to fetch prices for.
 * @returns A Promise resolving to an array of SFCCProductPrice or null if no prices are found.
 */
const fetchSFCCPrices = async (skus: string[]): Promise<SFCCProductPrice[] | null> => {
  const skuChunks = chunk(skus, 24);
  let prices: SFCCProductPrice[] = [];

  for (const skuChunk of skuChunks) {
    const response = await getPricesBySkus<SFCCPricingSearchResult>(skuChunk);
    if (!response) {
      continue;
    }

    if ('data' in response && response.data) {
      prices = prices.concat(response.data as SFCCProductPrice[]);
    } else if ('currency' in response) {
      prices.push(response as SFCCProductPrice);
    }
  }

  return prices.length > 0 ? prices : null;
};

/**
 * Fetches prices for standard product IDs with chunking.
 * @param productIds - An array of numeric product IDs to fetch prices for.
 * @returns A Promise resolving to an array of Price or null if no prices are found.
 */
const fetchStandardPrices = async (productIds: number[]): Promise<Price[] | null> => {
  const idChunks = chunk(productIds, 24); // Chunk the product IDs into groups of 24
  let prices: Price[] = [];

  for (const idChunk of idChunks) {
    const response = await getPricesByIds<Price[]>(idChunk, ProductType.Product);
    if (response) {
      prices = prices.concat(response);
    }
  }

  return prices.length > 0 ? prices : null;
};
